// Client for the `backbar` edge function (docs/academy-split/BACKBAR-API.md).
//
//   POST ${VITE_SUPABASE_URL}/functions/v1/backbar  { action, ...params }
//   → { ok: true, data } | { ok: false, error: { code, message } }
//
// `call` returns `data` or throws a BackbarError with the server's code +
// message. `not_configured` is a normal state (a secret is not set yet), so
// pages show it as a calm "Not connected yet" note — never an error toast.
import { supabase } from './supabase';

export type BackbarCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'conflict'
  | 'not_configured'
  | 'internal'
  | 'network'
  | string;

export class BackbarError extends Error {
  code: BackbarCode;
  details?: unknown;
  constructor(code: BackbarCode, message: string, details?: unknown) {
    super(message);
    this.name = 'BackbarError';
    this.code = code;
    this.details = details;
  }
}

const BASE = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(/\/+$/, '');
const ANON = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';

export async function call<T = unknown>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new BackbarError('unauthorized', 'Sign in again to continue.');

  let res: Response;
  try {
    res = await fetch(`${BASE}/functions/v1/backbar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: ANON,
      },
      body: JSON.stringify({ action, ...params }),
    });
  } catch {
    throw new BackbarError('network', 'The connection dropped. Check the internet and try again.');
  }

  type Envelope = { ok?: boolean; data?: T; error?: { code?: string; message?: string; details?: unknown } };
  let body: Envelope | null = null;
  try {
    body = (await res.json()) as Envelope;
  } catch {
    body = null;
  }
  if (!body || typeof body !== 'object') {
    throw new BackbarError('internal', `The server answered with ${res.status} and no details.`);
  }
  if (body.ok) return body.data as T;
  const err = body.error ?? {};
  throw new BackbarError(err.code ?? 'internal', err.message ?? 'Something went wrong.', err.details);
}

export function isNotConfigured(err: unknown): boolean {
  return err instanceof BackbarError && err.code === 'not_configured';
}

/** Plain-words message for any thrown value. */
export function errorText(err: unknown): string {
  if (err instanceof BackbarError) return err.message;
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
  return 'Something went wrong.';
}
